import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/Button.tsx";
import { t } from "i18next";
import { cn } from "@edusogno/lib/utils";
import { useSEO } from "@/contexts/SeoContext.tsx";
import { useToggle } from "@/utils/hooks/useToggle.ts";
import { useLocation } from "react-router-dom";
import { LoginUserItem } from "@/types";
import { useUserExercises } from "@/data/exercises.ts";
import { useMe, useNotifications } from "@/data/user.ts";
import { updateExercisesIsNewProperty } from "@/utils/notifications";
import {
  MIXPANEL_EVENT_TYPES,
  maixpanelTrackEventProps,
} from "@/mixpanel/MixpanelUtils";
import {
  Tabs,
  TabsList,
  TabsContent,
  TabsTrigger,
} from "@edusogno/primitives/tabs";
import {
  Select,
  SelectItem,
  SelectGroup,
  SelectValue,
  SelectContent,
  SelectTrigger,
} from "@/components/ui/Select.tsx";
import List from "@/components/ui/List.tsx";
import DoneList from "@/components/exercises/DoneList.tsx";
import TodoList from "@/components/exercises/TodoList.tsx";
import useGlobals from "@/utils/hooks/useGlobals.ts";
import PageHeading from "@/components/ui/PageHeading.tsx";
import DashboardContentLayout from "@/layouts/DashboardContentLayout.tsx";

export default function Exercises() {
  const { me } = useMe();
  const { notifications } = useNotifications();
  const { exercises, isLoading } = useUserExercises();
  const { setTitle, setDescription } = useSEO();

  const [filter, setFilter] = useState<string>("all");
  const [currentTab, setCurrentTab] = useState<string>("todo");

  const exercisesTodo = useMemo(() => {
    try {
      const notificationsItems = notifications?.items || [];
      const exercisesToDo = exercises?.toDo || [];

      const updatedExercises = updateExercisesIsNewProperty(
        notificationsItems,
        exercisesToDo,
      );

      return updatedExercises.filter(
        (e) =>
          e.exercises_statuses?.status === "not started" ||
          !e.exercises_statuses?.status,
      );
    } catch (error) {
      console.error("Error updating exercises:", error);
      return exercises?.toDo || [];
    }
  }, [exercises?.toDo, notifications?.items]);

  const exercisesStandby = useMemo(
    () =>
      exercises?.toDo.filter(
        (e) => e.exercises_statuses?.status === "in progress",
      ) || [],
    [exercises?.toDo],
  );

  const { setGlobalState } = useGlobals();
  const [expires, toggle] = useToggle(false);
  const location = useLocation();
  const state = location?.state as { tab: string };

  useEffect(() => {
    setGlobalState("backgroundClass", "bg-brand-background");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterOptions = [
    { label: t("exercises.all"), value: "all" },
    { label: "Writing", value: "writing" },
    { label: "Reading", value: "reading" },
    { label: "Listening", value: "listening" },
    { label: "Grammar", value: "grammar" },
    { label: "Speaking", value: "speaking" },
    { label: "Comprehension", value: "comprehension" },
  ];

  useEffect(() => {
    setTitle(
      `${me?.item.user.first_name} ${me?.item.user.last_name} | Exercises `,
    );
    setDescription("Exercises Page Page");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  useEffect(() => {
    if (state?.tab) {
      setCurrentTab(state.tab);
    }
  }, [state]);

  // mixpanel integration
  const mixpanelPropsData = JSON.stringify(
    maixpanelTrackEventProps("Exercise", me?.item?.user as LoginUserItem),
  );

  return (
    <DashboardContentLayout loading={isLoading}>
      <PageHeading
        title={t("menu.exercises", { defaultValue: "Exercises" })}
        subtitle={t("exercises.subtitle")}
      />

      <Tabs
        value={currentTab}
        className="h-[--content-height]"
        defaultValue="todo"
        onValueChange={setCurrentTab}
      >
        <div className="flex flex-row items-center justify-end w-full gap-2 pt-8 mb-4 lg:gap-4 lg:justify-end ">
          <Button
            data-track-props={mixpanelPropsData}
            data-track-eventtype={MIXPANEL_EVENT_TYPES.EXERCISE_EXPIRES_FIRST}
            className={cn(
              "hidden md:flex bg-white text-brand-secondary  md:w-fit font-medium p-0 h-[40px] md:h-[40px]  text-sm max-w-full px-1 md:px-4 hover:bg-white",
              {
                "hidden pointer-events-none":
                  currentTab === "done" || currentTab == "standby",
              },
            )}
            variant={"default"}
            onClick={toggle}
          >
            {expires ? t("exercises.default") : t("exercises.expires_first")}
          </Button>

          <TabsList className="p-0 rounded-[7px] h-[40px] md:h-[40px]">
            <TabsTrigger
              data-track-props={mixpanelPropsData}
              data-track-eventtype={MIXPANEL_EVENT_TYPES.EXERCISE_TO_DO_BUTTON}
              value="todo"
              className="active:border-none active:outline-0 h-[40px] md:h-[40px] px-4 lg:w-24 data-[state=active]:bg-brand-deep text-brand-deep text-sm rounded-[7px] data-[state=active]:text-white"
            >
              {t("exercises.todo")}
            </TabsTrigger>

            <TabsTrigger
              data-track-props={mixpanelPropsData}
              data-track-eventtype={MIXPANEL_EVENT_TYPES.EXERCISE_DONE_BUTTONE}
              value="done"
              className="active:border-none active:outline-0 h-[40px] md:h-[40px] px-3 lg:w-24 data-[state=active]:bg-brand-deep text-brand-deep text-sm rounded-[7px] data-[state=active]:text-white"
            >
              {t("exercises.done")}
            </TabsTrigger>

            <TabsTrigger
              value="standby"
              className="active:border-none active:outline-0 h-[40px] md:h-[40px] px-3 lg:w-24 data-[state=active]:bg-brand-deep text-brand-deep text-sm rounded-[7px] data-[state=active]:text-white"
            >
              {t("exercises.standby")}
            </TabsTrigger>
          </TabsList>

          <Select defaultValue={"all"} onValueChange={setFilter}>
            <SelectTrigger
              data-track-props={mixpanelPropsData}
              data-track-eventtype={
                MIXPANEL_EVENT_TYPES.EXERCISE_FILTER_DROPDOWN
              }
              showIcon={false}
              className="flex flex-none justify-between outline-none  px-2 text-sm  md:flex-none w-[112px]   md:w-[180px] h-[40px] md:h-[40px] items-centerbg-light border-0 text-brand"
            >
              <SelectValue placeholder="Filter value" className="text-sm" />
            </SelectTrigger>

            <SelectContent>
              <SelectGroup>
                {filterOptions.map((option) => (
                  <SelectItem
                    data-track-props={mixpanelPropsData}
                    data-track-eventtype={`Exercise_Filtered_${option.label}`}
                    key={option.value}
                    value={option.value}
                    className="text-sm"
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <TabsContent
          value="todo"
          className="lg:max-h-[calc(100%_-_52px)] max-h-[calc(100%_-_64px)] h-full"
        >
          <List className="overflow-y-auto pr-0 !max-h-full">
            <TodoList
              exercises={
                exercisesTodo?.filter(
                  (r) =>
                    r?.modality?.toLowerCase() === filter ||
                    r?.type?.toLowerCase() === filter ||
                    filter === "all",
                ) ?? []
              }
              deadline={expires}
            />
            <div className="pb-6"></div>
          </List>
        </TabsContent>

        <TabsContent
          value="done"
          className="lg:max-h-[calc(100%_-_52px)] max-h-[calc(100%_-_64px)] h-full"
        >
          <List className="overflow-y-auto pr-0 !max-h-full">
            <DoneList
              me={me?.item}
              done={exercises?.done.length ?? 0}
              total={
                (exercises?.done.length ?? 0) + (exercises?.toDo.length ?? 0)
              }
              exercises={
                exercises?.done.filter(
                  (r) =>
                    r?.modality?.toLowerCase() === filter ||
                    r?.type?.toLowerCase() === filter ||
                    filter === "all",
                ) ?? []
              }
              countCorrections={exercises?.count_corrections || []}
              countBookedCorrections={exercises?.count_booked_corrections || []}
            />

            <div className="pb-6"></div>
          </List>
        </TabsContent>

        <TabsContent
          value="standby"
          className="lg:max-h-[calc(100%_-_52px)] max-h-[calc(100%_-_64px)] h-full"
        >
          <List className="overflow-y-auto pr-0 !max-h-full">
            <TodoList exercises={exercisesStandby} deadline={expires} />
            <div className="pb-6"></div>
          </List>
        </TabsContent>
      </Tabs>
    </DashboardContentLayout>
  );
}
